import { useLayoutEffect, useRef } from "react";

import { Button } from "@/shared/ui/button";
import { Checkbox } from "@/shared/ui/checkbox";
import { DateField } from "@/shared/ui/date-field";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Label } from "@/shared/ui/label";
import { useLanguage } from "@/shared/hooks/use-language";

interface ImportConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** ISO `YYYY-MM-DD`, owned by `useCsvImport` — the dialog renders the field but never
   *  holds the value, so a date typed here is the same one the dispatch reads. */
  referenceDate: string;
  onReferenceDateChange: (value: string) => void;
  /** `fieldErrors.referenceTime` from the pipeline. The pre-submit check that sets it
   *  runs inside `submit`, which this dialog calls, so the message lands on the field
   *  that is still on screen rather than in the panel's banner. */
  referenceDateError?: string;
  isComplete: boolean;
  onIsCompleteChange: (value: boolean) => void;
  /** The pipeline's `submit`. A submit that bails on the date check changes nothing the
   *  panel can see, so the dialog stays open with the field error showing; the panel
   *  closes it once the submit actually proceeds — see `ImportPanel`'s own effect. Every
   *  field here stays enabled through that one render: `submit` itself guards
   *  re-entrancy (it bails at the top while it is already running), and the panel's
   *  effect unmounts this dialog the instant a dispatch actually starts, so a disabled
   *  state wired to that same flag would never paint a frame a user could see. */
  onConfirm: () => void;
}

/**
 * The last step before a holdings file is imported: it confirms the date the positions
 * are held on, and asks whether the file is the portfolio's complete statement.
 *
 * The completeness declaration closes every position the file does not list, which is the
 * one irreversible thing this import can do, so it is not a checkbox sitting in the
 * pre-upload form where a file drop is the next action. The date moved in with it because
 * the two are one decision: the date is the instant the declaration is made at, and the
 * closing rows it books are dated by it.
 *
 * Only a request type whose config declares completeness renders this dialog
 * (`IMPORT_TYPE_CONFIG.declaresCompleteness`) — a blotter is a list of events and states
 * nothing about what is held, so the trade flow starts straight from its own button.
 */
export const ImportConfirmDialog = ({
  open,
  onOpenChange,
  referenceDate,
  onReferenceDateChange,
  referenceDateError,
  isComplete,
  onIsCompleteChange,
  onConfirm,
}: ImportConfirmDialogProps) => {
  const { t } = useLanguage();

  // Read through a ref, the same way `useCsvImport` protects `onImported`: this effect
  // must fire on an open and on nothing else. A caller passing an inline arrow — a fresh
  // identity every render — would otherwise re-run it on every render and pin the box to
  // unchecked, so a click could never take.
  const onIsCompleteChangeRef = useRef(onIsCompleteChange);
  onIsCompleteChangeRef.current = onIsCompleteChange;

  // Every open starts unchecked, never at whatever the last open left behind. A user who
  // ticked the box, cancelled to re-read the file, and came back would otherwise confirm
  // a declaration they never re-made on this pass — and the thing being confirmed closes
  // positions. The date keeps its value across an open: it is a fact about the file, and
  // re-deriving today's date would silently discard a date the user typed.
  //
  // A layout effect, not a passive one: a passive effect runs after the browser has
  // painted, so the box renders ticked for a frame as the dialog fades in and only then
  // clears. Nothing that closes positions may ever be shown as already agreed to, however
  // briefly — and a frame of the old value is also a frame a test can read, which would
  // make this rule's own coverage depend on when the assertion happened to land.
  useLayoutEffect(() => {
    if (open) onIsCompleteChangeRef.current(false);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="import-confirm-dialog">
        <DialogHeader>
          <DialogTitle>{t("portfolio.import.confirmTitle")}</DialogTitle>
          <DialogDescription>{t("portfolio.import.confirmDescription")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <DateField
            id="import-reference-date"
            label={t("portfolio.import.referenceTimeLabel")}
            hint={t("portfolio.import.referenceTimeHint")}
            value={referenceDate}
            error={referenceDateError}
            onChange={onReferenceDateChange}
            data-testid="import-reference-date"
          />

          {/* The label and the hint sit beside the control, not inside it: the hint is the
              sentence that says what checking this actually does, and a control's own
              accessible name should stay short enough to read as a name. */}
          <div className="flex items-start gap-3 rounded-lg border border-border p-4">
            <Checkbox
              id="import-complete"
              data-testid="import-complete-checkbox"
              checked={isComplete}
              onCheckedChange={(checked) => onIsCompleteChange(checked === true)}
              aria-describedby="import-complete-hint"
              className="mt-0.5"
            />
            <div className="space-y-1">
              <Label htmlFor="import-complete" className="cursor-pointer">
                {t("portfolio.import.completeLabel")}
              </Label>
              <p
                id="import-complete-hint"
                className="text-xs text-muted-foreground"
                data-testid="import-complete-hint"
              >
                {t("portfolio.import.completeHint")}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="import-confirm-cancel"
          >
            {t("portfolio.cancel")}
          </Button>
          <Button onClick={onConfirm} data-testid="import-confirm">
            {t("portfolio.import.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
